/**
 * OpenOwnershipModal — Tier 0 slice-local component (SCRUM-172).
 *
 * Records a new ownership for a property that has none (the empty-state
 * "Add owner" action). Mirrors the `UnitStatusModal` dialog contract
 * (scrim + role="dialog" + useFocusTrap + Esc + inline role="alert" +
 * polite SR announce). Fields match `openOwnershipSchema`: owner
 * (picker over org members → userId), share, effective-from date
 * (required), plus optional contract name / reference / expiry. While the
 * backend runs in Lite mode (`OWNERSHIP_LITE_MODE`) the share is fixed at
 * 100% and rendered read-only — `open()` rejects anything but 100. No
 * If-Match (this creates the first ownership).
 */

import * as React from 'react';
import { Button, TextField } from '@sparta/ui-kit';
import { FilterSel } from './FilterSel.js';
import { dateOnlyToIso } from './ownershipDates.js';
import { OWNERSHIP_LITE_MODE } from './ownershipMode.js';
import { useFocusTrap } from '../../../shared/hooks/useFocusTrap.js';
import { useSrAnnouncement } from '../../../shared/hooks/useSrAnnouncement.js';
import { useOpenOwnership } from '../api/use-ownership-mutations.js';
import type { OrgUserOption } from '../api/use-org-users.js';

export interface OpenOwnershipModalProps {
  readonly propertyId: string;
  readonly members: readonly OrgUserOption[];
  readonly onClose: () => void;
}

function parseShare(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= 100 ? n : null;
}

export function OpenOwnershipModal({
  propertyId,
  members,
  onClose,
}: OpenOwnershipModalProps): React.ReactElement {
  const [userId, setUserId] = React.useState('');
  const [shareRaw, setShareRaw] = React.useState('100');
  const [effectiveFrom, setEffectiveFrom] = React.useState('');
  const [contractName, setContractName] = React.useState('');
  const [contractReference, setContractReference] = React.useState('');
  const [expiresAt, setExpiresAt] = React.useState('');
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const { mutateAsync, status, errorMessage, reset } = useOpenOwnership();
  const sr = useSrAnnouncement();

  useFocusTrap(dialogRef, { initialFocusRef: cancelRef });

  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Lite: share is fixed at 100% (open() rejects anything else). Pro will
  // honour a 1–100 entry — keep the parser wired for that path.
  const share = OWNERSHIP_LITE_MODE ? 100 : parseShare(shareRaw);
  const effectiveIso = dateOnlyToIso(effectiveFrom);
  const expiresIso = dateOnlyToIso(expiresAt);
  const isValid =
    userId.length > 0 && share !== null && effectiveIso !== undefined;

  const onAnyChange = (): void => {
    if (errorMessage !== null) reset();
  };

  const handleSave = async (): Promise<void> => {
    if (!isValid || share === null || effectiveIso === undefined) return;
    try {
      await mutateAsync({
        propertyId,
        body: {
          userId,
          share,
          effectiveFrom: effectiveIso,
          ...(contractName.length > 0 ? { contractName } : {}),
          ...(contractReference.length > 0 ? { contractReference } : {}),
          ...(expiresIso !== undefined ? { expiresAt: expiresIso } : {}),
        },
      });
      sr.polite('Owner recorded');
      onClose();
    } catch {
      // errorMessage drives the inline role="alert"; modal stays open.
    }
  };

  const ownerOptions = [
    { id: '', label: 'Select an owner…' },
    ...members.map((m) => ({ id: m.id, label: m.name })),
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ background: 'var(--bg-overlay)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {sr.regions}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="open-ownership-title"
        aria-describedby="open-ownership-subtitle"
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: 'var(--bg-surface)', boxShadow: 'var(--shadow-xl)' }}
      >
        <h2
          id="open-ownership-title"
          className="m-0 text-lg font-semibold"
          style={{ color: 'var(--fg-primary)' }}
        >
          Add owner
        </h2>
        <p
          id="open-ownership-subtitle"
          className="mt-1 mb-4 text-sm"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Record who owns this property, their share, and when ownership
          began.
        </p>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <span
              className="text-xs font-bold uppercase"
              style={{ color: 'var(--fg-tertiary)', letterSpacing: '0.05em' }}
            >
              Owner
            </span>
            <FilterSel<string>
              label="Owner"
              value={userId}
              options={ownerOptions}
              onChange={(next) => {
                onAnyChange();
                setUserId(next);
              }}
            />
          </div>

          {OWNERSHIP_LITE_MODE ? (
            <div className="flex flex-col gap-1.5">
              <span
                className="text-xs font-bold uppercase"
                style={{ color: 'var(--fg-tertiary)', letterSpacing: '0.05em' }}
              >
                Share
              </span>
              <div
                className="rounded-lg px-3 py-2 text-sm"
                style={{
                  background: 'var(--bg-sunken)',
                  color: 'var(--fg-secondary)',
                  border: '1px solid var(--border-subtle)',
                }}
              >
                100% — sole owner
              </div>
              <span className="text-xs" style={{ color: 'var(--fg-tertiary)' }}>
                Partial ownership shares arrive with Pro.
              </span>
            </div>
          ) : (
            <TextField
              label="Share (%)"
              type="number"
              min={1}
              max={100}
              step={1}
              inputMode="numeric"
              value={shareRaw}
              error={
                shareRaw.length > 0 && share === null
                  ? 'Enter a whole number from 1 to 100.'
                  : undefined
              }
              onChange={(e) => {
                onAnyChange();
                setShareRaw(e.target.value);
              }}
            />
          )}

          <TextField
            label="Owner since"
            type="date"
            value={effectiveFrom}
            onChange={(e) => {
              onAnyChange();
              setEffectiveFrom(e.target.value);
            }}
          />

          <TextField
            label="Contract name (optional)"
            value={contractName}
            onChange={(e) => {
              onAnyChange();
              setContractName(e.target.value);
            }}
          />

          <TextField
            label="Contract reference (optional)"
            value={contractReference}
            onChange={(e) => {
              onAnyChange();
              setContractReference(e.target.value);
            }}
          />

          <TextField
            label="Expires (optional)"
            type="date"
            value={expiresAt}
            onChange={(e) => {
              onAnyChange();
              setExpiresAt(e.target.value);
            }}
          />
        </div>

        {errorMessage !== null ? (
          <div
            role="alert"
            className="mt-4 rounded-lg p-3 text-sm"
            style={{
              background:
                'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
              color: 'var(--fg-danger-on-tint)',
              border:
                '1px solid color-mix(in oklab, var(--danger-500) 30%, transparent)',
            }}
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <Button ref={cancelRef} variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSave();
            }}
            disabled={!isValid || status === 'pending'}
          >
            {status === 'pending' ? 'Saving…' : 'Add owner'}
          </Button>
        </div>
      </div>
    </div>
  );
}
